import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Lightbulb, GraduationCap } from 'lucide-react';

const DefinitionsDisplay = ({ definitions }) => {
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [expandAll, setExpandAll] = useState(false);

  const definitionsArray = definitions ? Object.entries(definitions) : [];

  const toggleItem = (term) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(term)) {
      newExpanded.delete(term);
    } else {
      newExpanded.add(term);
    }
    setExpandedItems(newExpanded);
  };

  const handleExpandAll = () => {
    if (expandAll) {
      setExpandedItems(new Set());
    } else {
      setExpandedItems(new Set(definitionsArray.map(([term]) => term)));
    }
    setExpandAll(!expandAll);
  };

  if (definitionsArray.length === 0) {
    return (
      <div className="backdrop-blur-sm bg-white/40 rounded-xl sm:rounded-2xl shadow-sm border border-cream-300/50 p-4 sm:p-5 md:p-6">
        <div className="flex items-center space-x-2 sm:space-x-3 mb-3 sm:mb-4">
          <img 
            src="/book.png" 
            alt="Icon" 
            className="w-5 h-5 sm:w-6 sm:h-6 object-contain"
          />
          <h3 className="text-base sm:text-lg md:text-xl font-semibold text-brown-900">Definitions</h3>
        </div>
        <p className="text-xs sm:text-sm md:text-base text-brown-500">No definitions available</p>
      </div>
    );
  }

  return (
    <div className="backdrop-blur-sm bg-white/40 rounded-xl sm:rounded-2xl shadow-sm border border-cream-300/50 p-4 sm:p-5 md:p-6">
      
      {/* Header - Responsive */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-5 gap-2 sm:gap-0">
        <div className="flex items-center space-x-2 sm:space-x-3">
          <img src="/book.png" alt="Icon" 
            className="w-5 h-5 sm:w-6 sm:h-6 object-contain" 
          />
          <h3 className="text-base sm:text-lg md:text-xl font-semibold text-brown-900">Definitions</h3>
          <span className="text-xs sm:text-sm text-brown-500 font-medium">
            {definitionsArray.length} {definitionsArray.length === 1 ? 'term' : 'terms'}
          </span>
        </div>
        <button
          onClick={handleExpandAll}
          className="text-xs sm:text-sm text-brown-600 hover:text-brown-900 font-medium transition-colors self-start sm:self-auto"
        >
          {expandAll ? 'Collapse all' : 'Expand all'}
        </button>
      </div>

      {/* Definitions List - Responsive spacing */}
      <div className="space-y-2 sm:space-y-3">
        {definitionsArray.map(([term, definition], index) => {
          const isExpanded = expandedItems.has(term);
          
          // Check if definition is the new format (object with simple/technical)
          const isNewFormat = typeof definition === 'object' && definition.simple && definition.technical;
          
          return (
            <div 
              key={index}
              className="border border-cream-200/50 rounded-lg sm:rounded-xl overflow-hidden transition-all hover:border-brown-200/50"
            >
              {/* Term Header - Responsive */}
              <button
                onClick={() => toggleItem(term)}
                className="w-full flex items-center justify-between p-3 sm:p-4 bg-cream-50/30 hover:bg-cream-50/50 transition-all text-left"
              >
                <span className="text-sm sm:text-base md:text-lg font-semibold text-brown-900 flex-1 pr-3">
                  {term}
                </span>
                <div className="flex-shrink-0">
                  {isExpanded ? (
                    <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 text-brown-600" />
                  ) : (
                    <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-brown-600" />
                  )}
                </div>
              </button>

              {/* Definition Content - Responsive with animation */}
              {isExpanded && (
                <div className="bg-white/50 border-t border-cream-200/50 animate-slide-up">
                  
                  {isNewFormat ? (
                    // NEW FORMAT: Two-part definition
                    <div className="p-3 sm:p-4 space-y-3 sm:space-y-4">
                      
                      {/* Simple Explanation */}
                      <div className="bg-blue-50/30 rounded-lg p-3 sm:p-3.5 border border-blue-100/50">
                        <div className="flex items-start space-x-2 mb-1.5 sm:mb-2">
                          <Lightbulb className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                          <h4 className="text-xs sm:text-sm font-semibold text-blue-900">
                            Simple Explanation:
                          </h4>
                        </div>
                        <p className="text-xs sm:text-sm md:text-base text-blue-800 leading-relaxed ml-6">
                          {definition.simple}
                        </p>
                      </div>

                      {/* Technical Definition */}
                      <div className="bg-purple-50/30 rounded-lg p-3 sm:p-3.5 border border-purple-100/50">
                        <div className="flex items-start space-x-2 mb-1.5 sm:mb-2">
                          <GraduationCap className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                          <h4 className="text-xs sm:text-sm font-semibold text-purple-900">
                            For Exams:
                          </h4>
                        </div>
                        <p className="text-xs sm:text-sm md:text-base text-purple-800 leading-relaxed ml-6">
                          {definition.technical}
                        </p>
                      </div>
                    </div>
                  ) : (
                    // OLD FORMAT: Single definition (backward compatible)
                    <div className="p-3 sm:p-4">
                      <p className="text-xs sm:text-sm md:text-base text-brown-700 leading-relaxed">
                        {definition}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DefinitionsDisplay;